import type { IMyOutlookBriefingSchedule, IMyOutlookBriefingScheduleAnnotation, IMyOutlookBriefingScheduleUpdateAnnotation, MyOutlookBriefingScheduleDaySelection, MyOutlookBriefingScheduleMode, MyOutlookBriefingScheduleSource, MyOutlookBriefingScheduleReconciliationState, MyOutlookBriefingScheduleReadOutcome, MyOutlookBriefingScheduleUpdateOutcome, MyOutlookWeekday, } from "@augloop-types/my-outlook";

export const BRIEFING_SCHEDULE_INTERVALS = [15, 30, 60, 120, 240, 480] as const;

export type BriefingScheduleInterval = (typeof BRIEFING_SCHEDULE_INTERVALS)[number];
export type BriefingScheduleDaySelection = `${MyOutlookBriefingScheduleDaySelection}`;
export type BriefingScheduleMode = `${MyOutlookBriefingScheduleMode}`;
export type BriefingScheduleSource = `${MyOutlookBriefingScheduleSource}`;
export type BriefingScheduleReconciliationState = `${MyOutlookBriefingScheduleReconciliationState}`;
export type BriefingScheduleReadOutcome = `${MyOutlookBriefingScheduleReadOutcome}`;
export type BriefingScheduleUpdateOutcome = `${MyOutlookBriefingScheduleUpdateOutcome}`;
export type BriefingWeekday = `${MyOutlookWeekday}`;

export const BRIEFING_WEEKDAYS: readonly BriefingWeekday[] = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
];

export interface BriefingSchedule extends Omit<IMyOutlookBriefingSchedule, "version" | "daySelection" | "customDays" | "mode" | "intervalMinutes"> {
    readonly version: 1;
    readonly daySelection: BriefingScheduleDaySelection;
    readonly customDays?: BriefingWeekday[];
    readonly mode: BriefingScheduleMode;
    readonly intervalMinutes?: BriefingScheduleInterval;
}

export interface BriefingScheduleReadResponse extends Omit<IMyOutlookBriefingScheduleAnnotation, "contractVersion" | "outcome" | "source" | "schedule" | "reconciliationState"> {
    readonly contractVersion: 1;
    readonly outcome: BriefingScheduleReadOutcome;
    readonly source?: BriefingScheduleSource;
    readonly schedule?: BriefingSchedule;
    readonly reconciliationState?: BriefingScheduleReconciliationState;
}

export interface BriefingScheduleUpdateResponse extends Omit<IMyOutlookBriefingScheduleUpdateAnnotation, "contractVersion" | "outcome" | "schedule" | "reconciliationState"> {
    readonly contractVersion: 1;
    readonly outcome: BriefingScheduleUpdateOutcome;
    readonly schedule?: BriefingSchedule;
    readonly reconciliationState?: BriefingScheduleReconciliationState;
}

export function isValidBriefingSchedule(schedule: BriefingSchedule): boolean {
    if (schedule.version !== 1 ||
        !schedule.timeZone.trim() ||
        (schedule.daySelection === "Custom" && !schedule.customDays?.length)) {
        return false;
    }
    if (schedule.mode === "OnceDaily") {
        return isTimeOfDay(schedule.timeOfDay);
    }
    return (isTimeOfDay(schedule.activeStart) &&
        isTimeOfDay(schedule.activeEnd) &&
        schedule.activeStart !== schedule.activeEnd &&
        BRIEFING_SCHEDULE_INTERVALS.some(intervalMinutes => intervalMinutes === schedule.intervalMinutes));
}

function isTimeOfDay(value: string | undefined): boolean {
    return !!value && /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(value);
}
